import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { chargeKey } from '@dashboard/lib/queryKeys';
import { formatPriceForInput, formatPriceInput } from '@dashboard/services/formatting/price';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderNav from '@dashboard/components/CardHeaderNav';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import Dialog from '@dashboard/components/Dialog';
import { Label } from '@dashboard/components/Form';
import { Form } from '@dashboard/components/Formik';
import ProductPrice from '@dashboard/components/ProductPrice';
import Spinner from '@dashboard/components/Spinner';
import PaymentsBookingsDetail from './scenes/PaymentsBookingsDetail';
import PaymentsBookingsDetailInvoices from './scenes/PaymentsBookingsDetailInvoices';
import PaymentsBookingsDetailEmailLogs from './scenes/PaymentsBookingsDetailEmailLogs';
import PaymentsBookingsDetailTickets from './scenes/PaymentsBookingsDetailTickets';
import PaymentsBookingDetailsTransactions from './scenes/PaymentsBookingsDetailTransactions';
import { PaymentStatusBadges } from '@/dashboard/components/PaymentStatusBadges';
import Dash from '@/dashboard/components/Dash';

const getTabs = ({ chargeId, payment }) => [
  {
    name: 'Booking',
    href: `/orders/bookings/${chargeId}`,
    render: props => <PaymentsBookingsDetail {...props} chargeId={chargeId} payment={payment} />,
  },
  ...(payment.tickets && payment.tickets.length > 0
    ? [
        {
          name: 'Tickets',
          href: `/orders/bookings/${chargeId}/tickets`,
          render: props => (
            <PaymentsBookingsDetailTickets {...props} chargeId={chargeId} payment={payment} />
          ),
        },
      ]
    : []),
  {
    name: 'Emails',
    href: `/orders/bookings/${chargeId}/emails`,
    render: props => (
      <PaymentsBookingsDetailEmailLogs {...props} chargeId={chargeId} payment={payment} />
    ),
  },
  {
    name: 'Invoices',
    href: `/orders/bookings/${chargeId}/invoices`,
    render: props => (
      <PaymentsBookingsDetailInvoices {...props} chargeId={chargeId} payment={payment} />
    ),
  },
  {
    name: 'Transactions',
    href: `/orders/bookings/${chargeId}/transactions`,
    render: props => (
      <PaymentsBookingDetailsTransactions {...props} chargeId={chargeId} payment={payment} />
    ),
  },
];

const PaymentsBookingsDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const {
    params: { chargeId },
  } = useRouteMatch();
  const queryClient = useQueryClient();

  const {
    data: payment,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: chargeKey(chargeId),
    queryFn: () => fetcher(`/sellers/${sellerId}/charges/${chargeId}`, token),
  });

  const recordPaymentMutation = useMutation({
    mutationFn: async values => {
      return fetcher(`/charges/${chargeId}/record-payment`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeKey(chargeId) });
    },
  });

  if (isLoading) {
    return (
      <Card variant="root">
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!payment || error) {
    return (
      <Card variant="root">
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs({ chargeId, payment, refetch });

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <div className="flex justify-between w-full">
          <div className="space-y-1">
            <CardHeaderTitle variant="root">{payment.page?.name || <Dash />}</CardHeaderTitle>

            <ProductPrice>
              <ProductPrice.Price>
                {getPriceLabel({
                  amount: payment.amount,
                  currency: payment.currency,
                })}
              </ProductPrice.Price>
              <PaymentStatusBadges payment={payment} />
            </ProductPrice>
          </div>

          {(payment.status === 'partially_paid' || payment.status === 'unpaid') && (
            <div>
              <Dialog
                title="Record payment"
                trigger={<Button appearance="primary">Record payment</Button>}
                confirmButton={null}
                cancelButton={null}
              >
                {({ close }) => (
                  <div>
                    <div className="mb-4">
                      <Label>Amount due</Label>
                      <p className="font-semibold">
                        {getPriceLabel({ amount: payment.amountDue, currency: payment.currency })}
                      </p>
                    </div>

                    <Form
                      initialValues={{
                        amount: formatPriceForInput({
                          amount: payment.amountDue,
                          currency: payment.currency,
                          style: 'decimal',
                        }),
                      }}
                      fields={[
                        {
                          name: 'amount',
                          label: 'Amount paid',
                          type: 'number',
                        },
                      ]}
                      onSubmit={async values => {
                        try {
                          const amount = formatPriceInput(values.amount, payment.currency);
                          await recordPaymentMutation.mutateAsync({ amount: amount });
                          close();
                        } catch (error) {
                          return {
                            error: error.message,
                          };
                        }
                      }}
                      formSubmitAlign="end"
                    />
                  </div>
                )}
              </Dialog>
            </div>
          )}
        </div>

        <CardHeaderNav.Container tabs={tabs}>
          {({ currentTab }) =>
            tabs.map(tab => (
              <CardHeaderNav.Tab key={tab.name} tab={tab} current={currentTab.href === tab.href}>
                {tab.name}
              </CardHeaderNav.Tab>
            ))
          }
        </CardHeaderNav.Container>
      </CardHeader>

      <CardContent variant="compact">
        <Switch>
          {tabs.map(tab => (
            <Route key={tab.href} path={tab.href} render={tab.render} exact />
          ))}
        </Switch>
      </CardContent>
    </Card>
  );
};

export default PaymentsBookingsDetailOverview;
